import { Box, Stack, Typography, useTheme, alpha } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import RecyclingIcon from "@mui/icons-material/Recycling";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { type SvgIconComponent } from "@mui/icons-material";
import { MONTHLY_MEMBER_BADGE_LABEL, BrandBadge } from "@/features/brandBadge";
import {
  INHERITANCE_REUSE_PATH,
  INHERITANCE_REUSE_TITLE,
} from "@/features/inheritanceReuse/utils/constants";

/** 上品なミュートゴールド（バッジと調和させるアクセント色）。 */
const MUTED_GOLD = "#9a740c";

/** 月額会員特典タイル1件の定義。将来の特典追加はこの配列に足すだけでよい。 */
export interface MonthlyMemberTile {
  label: string;
  description: string;
  href: string;
  icon: SvgIconComponent;
}

/** セクション見出し・説明。 */
const SECTION_TITLE = "月額会員様限定";
const SECTION_CAPTION =
  "月額プランにご加入の会員様だけがご利用いただける特典機能です。";

/** 表示する月額会員特典タイル一覧。 */
const TILES: MonthlyMemberTile[] = [
  {
    label: INHERITANCE_REUSE_TITLE,
    description:
      "相続登記後に動きのない不動産から、売却・次相続の可能性が高い候補を厳選。",
    href: INHERITANCE_REUSE_PATH,
    icon: RecyclingIcon,
  },
];

/**
 * 特典タイル。既存 mypage の MenuButton（白カード・subtle shadow・角丸8）に近い見た目に、
 * ブランドティールのさりげないアクセント（左端の細線）で月額会員特典であることを控えめに示す。
 * タイポは安っぽさを避けるためウェイト・字間・行間・余白・色コントラストを整えている。
 */
const PremiumTile: React.FC<{ tile: MonthlyMemberTile }> = ({ tile }) => {
  const theme = useTheme();
  const teal = theme.palette.primary.main;
  const Icon = tile.icon;
  return (
    <Box
      component={RouterLink}
      to={tile.href}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        // SP対応: スマホは全幅、タブレット以上は380px。固定幅でも親をはみ出さないよう
        // maxWidth:100% と box-sizing を明示し、狭幅での横スクロールを防ぐ。
        width: { xs: "100%", sm: 380 },
        maxWidth: "100%",
        boxSizing: "border-box",
        p: { xs: 2, sm: 2.5, md: 3 },
        borderRadius: "8px",
        textDecoration: "none",
        color: theme.palette.black?.main ?? "#222222",
        backgroundColor: theme.palette.white?.main ?? "#FFFFFF",
        border: `1px solid ${theme.palette.border?.main ?? "#DDDDDD"}`,
        borderLeft: `3px solid ${teal}`,
        boxShadow: "0px 2px 8px -2px rgba(0, 0, 0, 0.25)",
        transition: "box-shadow 0.15s ease, border-color 0.15s ease",
        "&:hover": {
          boxShadow: "0px 6px 16px -4px rgba(0, 0, 0, 0.28)",
          borderColor: teal,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          width: 44,
          height: 44,
          borderRadius: "50%",
          backgroundColor: alpha(teal, 0.12),
        }}
      >
        <Icon sx={{ fontSize: 24, color: teal }} />
      </Box>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: "0.02em",
            lineHeight: 1.5,
            color: theme.palette.black?.main ?? "#222222",
          }}
        >
          {tile.label}
        </Typography>
        <Typography
          sx={{
            mt: 0.75,
            fontSize: 12.5,
            lineHeight: 1.8,
            color: theme.palette.darkgray?.main ?? "#777777",
          }}
        >
          {tile.description}
        </Typography>
      </Box>
      <ChevronRightIcon
        sx={{
          flexShrink: 0,
          color: theme.palette.gray?.main ?? "#AAAAAA",
          fontSize: 20,
        }}
      />
    </Box>
  );
};

/**
 * ホーム（mypage）の「月額会員様限定」特典セクション。
 * R.E. DATA Plus プロダクト本体のトンマナ（白/淡色カード・淡いグレー境界・角丸控えめ）に馴染ませつつ、
 * 見出しのタイポ（ウェイト・字間）と上品ゴールドバッジ・ミュートゴールドの王冠アイコンで
 * 特別感を品よく表現する（派手なグラデ/グロー/極太連発はしない）。
 * 表示可否（月額会員 かつ 機能フラグ）は呼び出し側で制御する。
 */
export const MonthlyMemberSection: React.FC = () => {
  const theme = useTheme();
  const teal = theme.palette.primary.main;

  return (
    <Box
      sx={{
        mt: 0,
        p: { xs: 3, md: 4 },
        borderRadius: "8px",
        backgroundColor: alpha(teal, 0.04),
        border: `1px solid ${theme.palette.border?.main ?? "#DDDDDD"}`,
        borderLeft: `3px solid ${teal}`,
      }}
    >
      {/* 見出し：上品ゴールドの王冠＋洗練された字面（weight600・やや広めの字間）＋ゴールドバッジ。 */}
      <Stack
        direction="row"
        gap={1.25}
        alignItems="center"
        flexWrap="wrap"
        sx={{ mb: 1 }}
      >
        <WorkspacePremiumIcon sx={{ color: MUTED_GOLD, fontSize: "22px" }} />
        <Typography
          variant="h4"
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            letterSpacing: "0.06em",
            color: theme.palette.black?.main ?? "#222222",
          }}
        >
          {SECTION_TITLE}
        </Typography>
        <BrandBadge label={MONTHLY_MEMBER_BADGE_LABEL} size="small" />
      </Stack>

      {/* 補足説明で見出し周りの質感を上げる。 */}
      <Typography
        sx={{
          mb: 3,
          fontSize: 13,
          lineHeight: 1.7,
          color: theme.palette.darkgray?.main ?? "#777777",
        }}
      >
        {SECTION_CAPTION}
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2.5}
        useFlexGap
        flexWrap="wrap"
      >
        {TILES.map((tile) => (
          <PremiumTile key={tile.href} tile={tile} />
        ))}
      </Stack>
    </Box>
  );
};
